import React, { useCallback, useMemo, useState } from 'react';
import { ActivityIndicator, AppState, Linking, RefreshControl, View } from 'react-native';
import { useQuery } from '@apollo/client';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import moment from 'moment';
import Toast from 'react-native-root-toast';
import { Feather } from '@expo/vector-icons';

import { GET_MOBILE_TRAINING_ASSIGNMENTS } from '../../graphql/training';
import { Text } from '../../components';
import { Card, EmptyState, Screen, SecondaryButton, SegmentedControl, Tag } from '../../design/components';
import { PEOPLE_TRAINING_URL } from '../../constants';
import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import {
	PEOPLE_TRAINING_ASSIGNMENT_STATUS,
	PEOPLE_TRAINING_OUTSTANDING_STATUSES,
	PEOPLE_TRAINING_PRACTICAL_OUTCOME,
	PEOPLE_TRAINING_PRACTICAL_OUTCOME_NAMES,
} from '../../../common/constants';
import { getEmployeeDisplayName, getTrainingOutcomeLabel } from './trainingDisplay';

const TRAINING_TAB = {
	OUTSTANDING: 'outstanding',
	COMPLETED: 'completed',
};

const COMPLETED_STATUSES = [
	PEOPLE_TRAINING_ASSIGNMENT_STATUS.PASSED,
	PEOPLE_TRAINING_ASSIGNMENT_STATUS.FAILED_REQUIRES_IN_PERSON,
	PEOPLE_TRAINING_ASSIGNMENT_STATUS.COMPLETED_IN_PERSON,
];

// Practical-specific wording: FAILED_REQUIRES_IN_PERSON comes from the "needs follow-up" outcome.
const OUTSTANDING_STATUS_LABELS = {
	[PEOPLE_TRAINING_ASSIGNMENT_STATUS.REQUIRED]: 'Required',
	[PEOPLE_TRAINING_ASSIGNMENT_STATUS.IN_PROGRESS]: 'In progress',
	[PEOPLE_TRAINING_ASSIGNMENT_STATUS.OVERDUE]: 'Overdue',
	[PEOPLE_TRAINING_ASSIGNMENT_STATUS.FAILED_REQUIRES_IN_PERSON]:
		PEOPLE_TRAINING_PRACTICAL_OUTCOME_NAMES[PEOPLE_TRAINING_PRACTICAL_OUTCOME.NEEDS_FOLLOW_UP],
};

const ATTENTION_STATUSES = [
	PEOPLE_TRAINING_ASSIGNMENT_STATUS.OVERDUE,
	PEOPLE_TRAINING_ASSIGNMENT_STATUS.FAILED_REQUIRES_IN_PERSON,
];

const TrainingListView = () => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const [activeTab, setActiveTab] = useState(TRAINING_TAB.OUTSTANDING);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const isOutstandingTab = activeTab === TRAINING_TAB.OUTSTANDING;
	const {
		data,
		error,
		loading,
		refetch,
	} = useQuery(GET_MOBILE_TRAINING_ASSIGNMENTS, {
		variables: {
			page: 0,
			perPage: 100,
			sortBy: isOutstandingTab ? 'dueAt' : 'resolvedAt',
			sortDirection: isOutstandingTab ? 1 : -1,
			filter: {
				statuses: isOutstandingTab ? PEOPLE_TRAINING_OUTSTANDING_STATUSES : COMPLETED_STATUSES,
			},
		},
		fetchPolicy: 'cache-and-network',
		notifyOnNetworkStatusChange: true,
	});

	const assignments = useMemo(() => {
		const items = data?.getTrainingAssignments?.items || [];
		if (isOutstandingTab) {
			return items.filter(assignment => (
				assignment?.assignmentStatus !== PEOPLE_TRAINING_ASSIGNMENT_STATUS.FAILED_REQUIRES_IN_PERSON ||
				assignment?.canCompletePracticalTraining
			));
		}

		return items.filter(assignment => (
			assignment?.assignmentStatus !== PEOPLE_TRAINING_ASSIGNMENT_STATUS.FAILED_REQUIRES_IN_PERSON ||
			!assignment?.canCompletePracticalTraining
		));
	}, [data?.getTrainingAssignments?.items, isOutstandingTab]);

	useFocusEffect(useCallback(() => {
		refetch().catch(() => {});
		// Browser training leaves this screen focused; refresh saved completion on return.
		const subscription = AppState.addEventListener('change', (nextAppState) => {
			if (nextAppState === 'active') {
				refetch().catch(() => {});
			}
		});
		return () => subscription.remove();
	}, [refetch]));

	const openPeopleTraining = useCallback(async () => {
		try {
			await Linking.openURL(PEOPLE_TRAINING_URL);
		}
		catch (linkError) {
			Toast.show('Unable to open Sentry People. Try again or open people.sentryvend.com in your browser.');
		}
	}, []);

	const refresh = useCallback(async () => {
		setIsRefreshing(true);
		try {
			await refetch();
		}
		finally {
			setIsRefreshing(false);
		}
	}, [refetch]);

	const openAssignment = useCallback((assignment) => {
		navigation.navigate('TrainingDetailView', {
			id: assignment.id,
			assignment,
		});
	}, [navigation]);

	const renderAssignment = (assignment) => {
		const employeeName = getEmployeeDisplayName(assignment?.employee);
		const statusLabel = isOutstandingTab
			? (OUTSTANDING_STATUS_LABELS[assignment?.assignmentStatus] || 'Required')
			: getTrainingOutcomeLabel(assignment);
		const needsAttention = isOutstandingTab && ATTENTION_STATUSES.includes(assignment?.assignmentStatus);
		const dueLabel = isOutstandingTab && assignment?.dueAt
			? `Due ${moment(assignment.dueAt).format('DD MMM YYYY')}`
			: '';
		const completedLabel = !isOutstandingTab && assignment?.resolvedAt
			? `Completed ${moment(assignment.resolvedAt).format('DD MMM YYYY')}`
			: '';
		const trainerName = !isOutstandingTab ? assignment?.latestResult?.trainer?.fullName : '';

		const title = assignment?.trainingModule?.title || 'Practical training';
		const showEmployee = Boolean(assignment?.canCompletePracticalTraining && employeeName);
		const dateLabel = dueLabel || completedLabel;
		const footer = [dateLabel, trainerName ? `Trainer: ${trainerName}` : ''].filter(Boolean).join(' · ');
		let statusTone = 'success';
		if (needsAttention) {
			statusTone = 'danger';
		}
		else if (isOutstandingTab) {
			statusTone = 'neutral';
		}

		return (
			<Card
				key={assignment.id}
				tone="raised"
				onPress={() => openAssignment(assignment)}
				accessibilityLabel={[title, showEmployee ? employeeName : '', statusLabel, footer].filter(Boolean).join(', ')}
			>
				<View style={styles.itemRow}>
					<View style={styles.itemContent}>
						<Text style={styles.itemTitle}>{title}</Text>
						{showEmployee && <Text style={styles.itemEmployee}>{employeeName}</Text>}
						<View style={styles.itemFooter}>
							<Tag label={statusLabel} tone={statusTone} />
							{Boolean(footer) && <Text style={styles.itemFooterText}>{footer}</Text>}
						</View>
					</View>
					<Feather name="chevron-right" size={22} color={colors.iconMuted} />
				</View>
			</Card>
		);
	};

	const onlineTrainingCount = Number(data?.getTrainingAssignments?.mobileOnlineCount) || 0;
	const showOnlineNotice = isOutstandingTab && onlineTrainingCount > 0;
	const emptyTitle = isOutstandingTab ? 'No outstanding checklist trainings' : 'No completed checklist trainings';
	const emptyText = isOutstandingTab
		? 'Practical trainings where you are the Responsible person will appear here.'
		: 'Practical trainings signed off by a trainer will appear here.';

	return (
		<Screen
			heading="Training"
			subheading="Practical checklist trainings where you are the Responsible person, plus trainings signed off for you. Online trainings are completed or followed up in Sentry People on the web."
			refreshControl={(
				<RefreshControl
					refreshing={isRefreshing}
					onRefresh={refresh}
					tintColor={colors.accent}
				/>
			)}
		>
			<SegmentedControl
				role="tab"
				options={[
					{ value: TRAINING_TAB.OUTSTANDING, label: 'Outstanding' },
					{ value: TRAINING_TAB.COMPLETED, label: 'Completed' },
				]}
				value={activeTab}
				onChange={setActiveTab}
			/>

			{
				Boolean(showOnlineNotice && !error) && (
					<Card>
						<Text style={styles.noticeTitle}>Online trainings</Text>
						<Text style={styles.noticeText}>
							{`You have ${onlineTrainingCount} online training${onlineTrainingCount === 1 ? '' : 's'} to complete or follow up on in Sentry People on the web.`}
						</Text>
						<Text style={styles.noticeText}>
							Items requiring in-person training need follow-up with a trainer and cannot be completed online.
						</Text>
						<Text style={styles.noticeText}>Sign in with the same username and password as the app.</Text>
						<SecondaryButton
							label="Open Sentry People"
							icon="external-link"
							style={styles.peopleButton}
							onPress={openPeopleTraining}
						/>
					</Card>
				)
			}

			{
				Boolean(loading && !assignments.length && !error) && (
					<View style={styles.loading} accessibilityLabel="Loading">
						<ActivityIndicator size="large" color={colors.accent} />
					</View>
				)
			}

			{
				Boolean(error) && (
					<EmptyState
						tone="error"
						title="Unable to load trainings"
						message="Pull down to try again."
						actionLabel="Try again"
						onAction={refresh}
					/>
				)
			}

			{
				Boolean(!error && !loading && !assignments.length) && (
					<EmptyState
						icon={isOutstandingTab ? 'check-circle' : 'award'}
						title={emptyTitle}
						message={emptyText}
					/>
				)
			}

			{
				assignments.length > 0 && (
					<View style={styles.list}>
						{assignments.map(renderAssignment)}
					</View>
				)
			}
		</Screen>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	noticeTitle: {
		...type.rowTitleSemibold,
		color: colors.text,
	},
	noticeText: {
		...type.secondary,
		color: colors.textSecondary,
		marginTop: space.xs,
	},
	peopleButton: {
		marginTop: space.md,
	},
	loading: {
		paddingVertical: space.xxl,
		alignItems: 'center',
	},
	list: {
		gap: space.sm,
	},
	itemRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.md,
	},
	itemContent: {
		flex: 1,
		gap: space.xs,
	},
	itemTitle: {
		...type.rowTitleSemibold,
		color: colors.text,
	},
	itemEmployee: {
		...type.secondary,
		color: colors.textSecondary,
	},
	itemFooter: {
		marginTop: space.xs,
		flexDirection: 'row',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: space.sm,
	},
	itemFooterText: {
		...type.caption,
		color: colors.textFaint,
	},
});

export default TrainingListView;
